import React from 'react';
import { Grid, Typography, Button } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import { useHookstate } from '@hookstate/core';
import type { UserListItem } from '../../interfaces/IWarehouseForm';
import { useLang } from '~/hooks/useLang';
import FilterAutocompleteFetch from '~/components/FilterAutocompleteFetch';
import ManagersList from '../ManagersList';

interface AssociateManagersProps {
  readonly managersList: readonly UserListItem[];
  readonly onAddManager: (user: any) => boolean;
  readonly onRemoveManager: (userId: number) => void;
  readonly disabledFields?: boolean;
}

export default function AssociateManagers({ 
  managersList, 
  onAddManager,
  onRemoveManager,
  disabledFields 
}: AssociateManagersProps) {
  const { translate } = useLang();
  const [selectedUserId, setSelectedUserId] = React.useState<number | null>(null);
  const [resetKey, setResetKey] = React.useState(0);
  const fullUserObject = useHookstate<any>(null);


  const handleAddManager = () => {
    const user = fullUserObject.get();
    if (user) {
      const success = onAddManager(user);
      if (success) {
        setSelectedUserId(null);
        fullUserObject.set(null);
        setResetKey(prev => prev + 1);
      }
    }
  };

  return (
    <Grid container xs={12} display="flex" flexDirection="column" spacing={2}>
      <Grid item container xs={12} spacing={2}>
        <Grid item xs={12}>
          <Typography variant="body2" color="text.secondary" gutterBottom>
            {translate('Select users who will have management permissions for this warehouse')}
          </Typography>
        </Grid>
        
        <Grid item xs={12} md={2}>
          <FilterAutocompleteFetch
            key={resetKey}
            label={translate('Select User')}
            urlFetch="/user/get"
            onFetchChange={(userId) => setSelectedUserId(userId)}
            disabled={disabledFields}
            size="small"
            dataRequest="user"
            resetValues={resetKey > 0}
            getFullObject={fullUserObject}
          />
        </Grid>
        
        <Grid item xs={12} md={2} display="flex" alignItems="center">
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={handleAddManager}
            disabled={!selectedUserId || disabledFields}
            fullWidth
            size="small"
            sx={{ height: '40px' }}
          >
            {translate('Add')}
          </Button>
        </Grid>
      </Grid>

      <Grid item container xs={8} spacing={2} mb={2}>
        <Grid item xs={12}>
          <Typography variant="subtitle1" gutterBottom sx={{ mt: 2 }}>
            {translate('Managers List')}
          </Typography>
        </Grid>
        <ManagersList 
          managersList={managersList} 
          onRemove={onRemoveManager}
        />
      </Grid>
    </Grid>
  );
}
